import { isNotNilOrEmpty } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { generateQueryString, parseQueryString } from "client_legacy/ndaw/urlHelper";

export enum HashNavigationRouteActionKind {
	ActivityFeed = 0,
	Announcement = 1,
	AnnouncementComment = 2,
	AppStore = 3,
	UserRequestDetails = 4,
	UserRequestResponseDetails = 5,
	OpenDocument = 6,
	KnowledgeItem = 7,
	NotificationSettings = 8,
	Event = 9,
	HubManagement = 10,
	HubManagementSpecificView = 11,
	UserDetails = 12,
	CommunityPost = 13,
	CommunityPostComment = 14,
	EventComment = 15,
	KnowledgeItemComment = 16,
	CommunityFeed = 17
}

export type RouteParameter<TKind extends HashNavigationRouteActionKind> =
	TKind extends HashNavigationRouteActionKind.ActivityFeed ? "batchId"
		: TKind extends HashNavigationRouteActionKind.Announcement ? "announcementId"
		: TKind extends HashNavigationRouteActionKind.CommunityPost ? "postId"
		: TKind extends HashNavigationRouteActionKind.KnowledgeItem ? "knowledgeItemId"
		: TKind extends HashNavigationRouteActionKind.Event ? "eventId"
		: TKind extends HashNavigationRouteActionKind.AnnouncementComment ? "announcementId" | "commentId"
		: TKind extends HashNavigationRouteActionKind.CommunityPostComment? "postId" | "commentId"
		: TKind extends HashNavigationRouteActionKind.UserRequestDetails ? "requestId"
		: TKind extends HashNavigationRouteActionKind.UserRequestResponseDetails ? "requestId"
		: TKind extends HashNavigationRouteActionKind.HubManagementSpecificView ? "hubItemKind" | "view"
		: TKind extends HashNavigationRouteActionKind.UserDetails ? "userId"
		: TKind extends HashNavigationRouteActionKind.EventComment ? "eventId" | "commentId"
		: TKind extends HashNavigationRouteActionKind.KnowledgeItemComment ? "knowledgeItemId" | "commentId"
		: TKind extends HashNavigationRouteActionKind.CommunityFeed ? "communityId"
		: never;

export type RouteParameters<TKind extends HashNavigationRouteActionKind> = Partial<Record<RouteParameter<TKind>, string>>;

export type QueryParameter<TKind extends HashNavigationRouteActionKind> =
	TKind extends HashNavigationRouteActionKind.OpenDocument ? "siteUrl" | "listId" | "itemId" : never;

export type QueryParameters<TKind extends HashNavigationRouteActionKind> = Partial<Record<QueryParameter<TKind>, string>>;

export type HashNavigationRouteMatch<TKind extends HashNavigationRouteActionKind = HashNavigationRouteActionKind> = {
	action: TKind;
	params: RouteParameters<TKind>;
	queryParams: QueryParameters<TKind>;
};

interface IHashNavigationRoute<TKind extends HashNavigationRouteActionKind = HashNavigationRouteActionKind> {
	match(hash: string): TryResult<HashNavigationRouteMatch<TKind>>;
	generateFragment(match: HashNavigationRouteMatch<TKind>): TryResult<string>;
}

enum PathSegmentKind {
	Static = 1,
	Parameter = 2
}

type StaticPathSegment = string | {
	kind: PathSegmentKind.Static;
	value: string;
};

type ParameterPathSegment<TName extends string = string, TIsOptional extends boolean = boolean> = {
	kind: PathSegmentKind.Parameter;
	name: TName;
	isOptional: TIsOptional;
	pattern?: RegExp;
};

type PathSegment<TName extends string = string> =
	| StaticPathSegment
	| ParameterPathSegment<TName>;

function parameter<TName extends string = string>(name: TName): ParameterPathSegment<TName>;
function parameter<TName extends string = string>(name: TName, isOptional: boolean): ParameterPathSegment<TName>;
function parameter<TName extends string = string>(name: TName, pattern: RegExp): ParameterPathSegment<TName>;
function parameter<TName extends string = string>(name: TName, isOptional: boolean, pattern: RegExp): ParameterPathSegment<TName>;
function parameter<TName extends string = string>(
	name: TName,
	isOptionalOrPattern?: boolean | RegExp,
	pattern?: RegExp
): ParameterPathSegment<TName> {
	let isOptional: boolean = false;
	if (typeof isOptionalOrPattern === "boolean") {
		isOptional = isOptionalOrPattern;
	} else {
		isOptional = false;
		pattern = isOptionalOrPattern;
	}

	return {
		kind: PathSegmentKind.Parameter,
		isOptional,
		name,
		pattern
	};
}

class PathHashNavigationRoute<TKind extends HashNavigationRouteActionKind> implements IHashNavigationRoute<TKind> {
	private readonly _action: TKind;
	private readonly _pathSegmentConfigurations: readonly PathSegment<RouteParameter<TKind>>[];
	private readonly _queryParameters: readonly QueryParameter<TKind>[];

	constructor(
		action: TKind,
		pathSegments: readonly PathSegment<RouteParameter<TKind>>[],
		queryParameters?: readonly QueryParameter<TKind>[]
	) {
		this._action = action;
		this._pathSegmentConfigurations = pathSegments;
		this._queryParameters = queryParameters ?? [];
	}

	public match(hash: string): TryResult<HashNavigationRouteMatch<TKind>> {
		if (hash.startsWith("#")) {
			hash = hash.slice(1);
		}

		if (!hash.startsWith("/")) {
			return {
				wasSuccessful: false
			};
		}

		hash = hash.slice(1);

		const [path, queryString] = hash.split("?", 2);
		const segmentValues: string[] = path.split("/")
			.filter(isNotNilOrEmpty);

		const routeParams: Partial<RouteParameters<TKind>> = {};

		let valueIndex: number = 0;
		for (let i = 0; i < this._pathSegmentConfigurations.length; i++) {
			const configuration: PathSegment<RouteParameter<TKind>> = this._pathSegmentConfigurations[i];
			const match: PathSegmentMatch<RouteParameter<TKind>> = this.matchSegments(
				segmentValues[valueIndex],
				configuration
			);

			switch(match.kind) {
				case "match":
					valueIndex++;
					break;
				case "no-match":
					return { wasSuccessful: false };
				case "skip":
					break;
				case "parameter":
					routeParams[match.parameter] = match.value;
					valueIndex++;
					break;
				default:
					never(match);
			}
		}

		if (valueIndex !== segmentValues.length) {
			return {
				wasSuccessful: false
			};
		}

		let queryParams: QueryParameters<TKind> = {};

		if (isNotNilOrEmpty(queryString)) {
			const parseResult: TryResult<QueryParameters<TKind>> = parseQueryString(
				queryString,
				this._queryParameters
			);

			if (parseResult.wasSuccessful) {
				queryParams = parseResult.result;
			} else {
				return {
					wasSuccessful: false
				};
			}
		}

		return {
			wasSuccessful: true,
			result: {
				action: this._action,
				params: routeParams,
				queryParams: queryParams
			}
		};
	}

	public generateFragment(match: HashNavigationRouteMatch<TKind>): TryResult<string> {
		if (match.action !== this._action) {
			return {
				wasSuccessful: false
			};
		}
		let result: string = "";

		const addSegmentValue = (value: string): void => {
			result += `/${value}`;
		};

		for (let pathSegment of this._pathSegmentConfigurations) {
			if (typeof pathSegment === "string" || pathSegment.kind === PathSegmentKind.Static) {
				if (typeof pathSegment !== "string") {
					pathSegment = pathSegment.value;
				}

				addSegmentValue(pathSegment);
			} else {
				const param: string | undefined = match.params[pathSegment.name] as unknown as string | undefined;
				if (isNotNilOrEmpty(param)) {
					addSegmentValue(param);
				} else {
					if (!pathSegment.isOptional) {
						return {
							wasSuccessful: false
						};
					}
				}
			}
		}

		if (Object.keys(match.queryParams).length > 0){
			result += `?${generateQueryString(match.queryParams)}`;
		}

		return {
			wasSuccessful: true,
			result
		};
	}

	private matchSegments(
		value: string | undefined,
		configuration: PathSegment<RouteParameter<TKind>>
	): PathSegmentMatch<RouteParameter<TKind>> {
		if (typeof value === "undefined") {
			const canBeSkipped: boolean = typeof configuration !== "string"
				&& configuration.kind === PathSegmentKind.Parameter
				&& configuration.isOptional;

			return {
				kind: canBeSkipped ? "skip" : "no-match"
			};
		}

		if (typeof configuration === "string" || configuration.kind === PathSegmentKind.Static) {
			if (typeof configuration !== "string") {
				configuration = configuration.value;
			}

			return {
				kind: value.toUpperCase() === configuration.toUpperCase() ? "match" : "no-match"
			};
		}

		if (!configuration.pattern || value.match(configuration.pattern)) {
			return {
				kind: "parameter",
				parameter: configuration.name,
				value: value
			};
		}

		return {
			kind: configuration.isOptional ? "skip" : "no-match"
		};
	}
}

type PathSegmentMatch<TParameter extends string> = {
	kind: "match";
} | {
	kind: "no-match";
} | {
	kind: "skip";
} | {
	kind: "parameter";
	parameter: TParameter;
	value: string;
};

const supportedRoutes: IHashNavigationRoute[] = [
	new PathHashNavigationRoute(
		HashNavigationRouteActionKind.NotificationSettings,
		["activityFeed", "settings"]
	),
	new PathHashNavigationRoute(
		HashNavigationRouteActionKind.ActivityFeed,
		["activityFeed", parameter("batchId", true, /^[0-9a-fA-F]{8}-(?:[0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$/)]
	),
	new PathHashNavigationRoute(
		HashNavigationRouteActionKind.Announcement,
		["hub", "announcements", parameter("announcementId", /^\d+$/)]
	),
	new PathHashNavigationRoute(
		HashNavigationRouteActionKind.CommunityPost,
		["hub", "communityPosts", parameter("postId", /^\d+$/)]
	),
	new PathHashNavigationRoute(
		HashNavigationRouteActionKind.KnowledgeItem,
		["hub", "knowledgeItems", parameter("knowledgeItemId", /^\d+$/)]
	),
	new PathHashNavigationRoute(
		HashNavigationRouteActionKind.Event,
		["hub", "events", parameter("eventId", /^\d+$/)]
	),
	new PathHashNavigationRoute(
		HashNavigationRouteActionKind.AnnouncementComment,
		["hub", "announcements", parameter("announcementId", /^\d+$/), "comment", parameter("commentId", /^\d+$/)]
	),
	new PathHashNavigationRoute(
		HashNavigationRouteActionKind.KnowledgeItemComment,
		["hub", "knowledgeItems", parameter("knowledgeItemId", /^\d+$/), "comment", parameter("commentId", /^\d+$/)]
	),
	new PathHashNavigationRoute(
		HashNavigationRouteActionKind.EventComment,
		["hub", "events", parameter("eventId", /^\d+$/), "comment", parameter("commentId", /^\d+$/)]
	),
	new PathHashNavigationRoute(
		HashNavigationRouteActionKind.CommunityPostComment,
		["hub", "communityPosts", parameter("postId", /^\d+$/), "comment", parameter("commentId", /^\d+$/)]
	),
	new PathHashNavigationRoute(
		HashNavigationRouteActionKind.CommunityFeed,
		["hub", "communities", parameter("communityId", /^\d+$/)]
	),
	new PathHashNavigationRoute(
		HashNavigationRouteActionKind.AppStore,
		["appStore"]
	),
	new PathHashNavigationRoute(
		HashNavigationRouteActionKind.UserRequestDetails,
		["userRequests", parameter("requestId", /^\d+$/)]
	),
	new PathHashNavigationRoute(
		HashNavigationRouteActionKind.UserRequestResponseDetails,
		["userRequests", parameter("requestId", /^\d+$/), "response"]
	),
	new PathHashNavigationRoute(
		HashNavigationRouteActionKind.OpenDocument,
		["sharepoint", "openDocument"],
		["siteUrl", "listId", "itemId"]
	),
	new PathHashNavigationRoute(
		HashNavigationRouteActionKind.HubManagement,
		["hubManagement"]
	),
	new PathHashNavigationRoute(
		HashNavigationRouteActionKind.HubManagementSpecificView,
		[
			"hubManagement",
			parameter("hubItemKind", /^announcement|event|knowledgeItem$/),
			parameter("view", /^overview|category|template$/)
		]
	),
	new PathHashNavigationRoute(
		HashNavigationRouteActionKind.UserDetails,
		[
			"userDetails",
			parameter("userId", /^\d+$/)
		]
	)
];

export function parseHash(hash: string): HashNavigationRouteMatch | null {
	for (const route of supportedRoutes) {
		const routeResult: TryResult<HashNavigationRouteMatch<HashNavigationRouteActionKind>> = route.match(hash);

		if (routeResult.wasSuccessful) {
			return routeResult.result;
		}
	}

	return null;
}

export function generateFragment(
	match: HashNavigationRouteMatch
): string | null {
	for (const route of supportedRoutes) {
		const routeResult: TryResult<string> = route.generateFragment(match);

		if (routeResult.wasSuccessful) {
			return routeResult.result;
		}
	}

	return null;
}

export function generateFragmentUrlForSharing(match: HashNavigationRouteMatch): string | null {
	const fragment: string | null = generateFragment(match);

	if (!fragment) {
		return null;
	}

	return `?fragment=${encodeURIComponent(fragment)}`;
}
